import { lazy, Suspense, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller, type Control, type UseFormSetValue } from 'react-hook-form';
import { tagClipSchema, MAX_CLIP_TAGS, type TagClipInput, type ClipTagColor } from '@proref/schemas';
import type { ClipPlayback, ClipStatus, ClipTag, EmbeddedClipTag } from '@proref/types';
import { TAG_COLOR_OPTIONS, tagChipStyle } from '../../lib/tag-colors';
import {
  useClipQuery,
  useTagClip,
  usePublishClip,
  usePinClip,
  useUnpinClip,
  useSetClipTags,
} from '../../hooks/use-clips';
import { useClipTagsQuery, useCreateClipTag, useDeleteClipTag } from '../../hooks/use-clip-tags';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { useSportsQuery, useRulesQuery } from '../../hooks/use-sports';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useAuth } from '../../contexts/auth-context';
import { Stepper, type StepperStep } from '../../components/shared/Stepper';
import { Button } from '../../components/shared/Button';
import { Toggle } from '../../components/shared/Toggle';
import { Input } from '../../components/shared/Input';
import { Spinner } from '../../components/shared/Spinner';
import { Field } from '../../components/shared/Field';
import { Icon } from '../../components/shared/Icons';
import type { VideoPlayerHandle } from '../../components/shared/VideoPlayer';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import { DeleteClipDialog } from './DeleteClipDialog';
import { UnpublishClipDialog } from './UnpublishClipDialog';
import { statusLabel } from './ClipsPage';

// Code-split Video.js out of the main bundle — it's ~600 KB and only needed in the clip
// editor (and later the referee player), never on login/home. Loads on demand the first time
// a clip with a signed playback URL renders the player. The type above is a static type-only
// import, so it doesn't pull the runtime module into the main chunk.
const VideoPlayer = lazy(() =>
  import('../../components/shared/VideoPlayer').then((m) => ({ default: m.VideoPlayer })),
);

// Form state — covers every editable field across the four authoring sections.
// `options` is an array of objects (useFieldArray needs an object shape, not primitives).
// `correct_index` indexes into `options` rather than carrying the value directly, so
// renaming an option doesn't desync the correct mark.
interface FormValues {
  title: string;
  description: string;
  rule_article_id: string;
  // Seconds (decimal) — the player pauses at this point. Converted to/from ms at the API edge.
  decision_timestamp_sec: number;
  options: { value: string }[];
  correct_index: number; // -1 when nothing selected yet
  commentary_text: string;
}

// Section 1 status badge — mirrors the design's "✓ Uploaded" pill, with extra states for
// the in-flight and failed cases since they're real states for an admin watching a clip
// land. tokens-only colours. A function (not a static map) since the label is translated.
function fileBadge(t: TFunction, status: ClipStatus): { label: string; klass: string; spin: boolean } {
  const klass: Record<ClipStatus, string> = {
    uploading: 'bg-accent-tint text-accent',
    processing: 'bg-accent-tint text-accent',
    ready: 'bg-success-tint text-success',
    failed: 'bg-danger-tint text-danger',
  };
  const spin: Record<ClipStatus, boolean> = {
    uploading: true,
    processing: true,
    ready: false,
    failed: false,
  };
  return { label: t(`admin.clips.editor.fileBadge.${status}`), klass: klass[status], spin: spin[status] };
}

export function ClipEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { role, user } = useAuth();
  const isSuper = role === 'super_admin';

  const clipQuery = useClipQuery(id);
  const sportsQuery = useSportsQuery();
  // Federations is only available to super_admin — fed_admin sees their own federation name
  // via the AuthUser; we skip the fetch there to avoid a 403.
  const federationsQuery = useFederationsQuery(isSuper);

  // Find the sport object for this clip so we can fetch its rule-book by slug.
  const clip = clipQuery.data;
  const sport = useMemo(
    () => sportsQuery.data?.find((s) => s.id === clip?.sport_id),
    [sportsQuery.data, clip?.sport_id],
  );
  const rulesQuery = useRulesQuery(sport?.slug);

  // Resolve the display name for the clip's federation. super_admin reads from the federations
  // list; fed_admin's tenancy is on the AuthUser (the /federations endpoint is super_admin-only).
  const federationName = isSuper
    ? federationsQuery.data?.find((f) => f.id === clip?.federation_id)?.name ?? '—'
    : user?.federation_name ?? '—';

  if (clipQuery.isLoading) {
    return (
      <div className="grid place-items-center py-24">
        <Spinner className="w-6 h-6 text-ink-3" />
      </div>
    );
  }

  if (clipQuery.isError || !clip) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(clipQuery.error, t('admin.clips.review.couldNotLoad'))}
      </div>
    );
  }

  return (
    <ClipEditorInner
      clip={clip}
      onBack={() => navigate('/admin/clips')}
      isSuper={isSuper}
      sportSlug={sport?.slug}
      rules={rulesQuery.data ?? []}
      federationName={federationName}
    />
  );
}

// Split the form half out so hooks can be unconditional once the clip is loaded. Without
// this split, every form hook (useForm, useFieldArray) would have to live above the loading
// guards, which means resetting them with the loaded clip's values is a useEffect dance.
interface InnerProps {
  clip: ClipPlayback;
  onBack: () => void;
  isSuper: boolean;
  sportSlug: string | undefined;
  rules: { id: string; article_number: string; title: string }[];
  federationName: string;
}

function ClipEditorInner({ clip, onBack, rules, federationName }: InnerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tag = useTagClip(clip.id);
  const publish = usePublishClip(clip.id);
  const pin = usePinClip(clip.id);
  const unpin = useUnpinClip(clip.id);
  // One state slot for both destructive dialogs — they're mutually exclusive (a clip is
  // either live → unpublish, or draft → delete). Mode toggles between 'delete' and 'unpublish'.
  const [destructive, setDestructive] = useState<'delete' | 'unpublish' | null>(null);

  // Default values map the API shape onto the form shape. The admin read path
  // (getClipById for federation_admin / super_admin) returns correct_decision and
  // commentary_text alongside the public columns, so the editor can pre-fill them —
  // referees never see those fields. correct_index is resolved by finding the saved
  // correct_decision string in the options array; -1 falls back to "nothing picked".
  const initialOptions =
    clip.options.length >= 2 ? clip.options : ['', ''];
  const initialCorrectIndex =
    clip.correct_decision != null
      ? initialOptions.findIndex((o) => o === clip.correct_decision)
      : -1;
  const form = useForm<FormValues>({
    defaultValues: {
      title: clip.title,
      description: clip.description ?? '',
      rule_article_id: clip.rule_article_id ?? '',
      decision_timestamp_sec: clip.decision_timestamp_ms ? clip.decision_timestamp_ms / 1000 : 0,
      options: initialOptions.map((v) => ({ value: v })),
      correct_index: initialCorrectIndex,
      commentary_text: clip.commentary_text ?? '',
    },
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields, errors },
  } = form;

  const options = useFieldArray({ control, name: 'options' });
  const watchedRule = watch('rule_article_id');
  const watchedTitle = watch('title');
  const correctIndex = watch('correct_index');
  const decisionSec = watch('decision_timestamp_sec');
  const watchedOptions = watch('options');
  const watchedCommentary = watch('commentary_text');

  const selectedRule = rules.find((r) => r.id === watchedRule);

  // Stepper state is derived purely from form completion — each step ticks to "completed"
  // as the admin fills in its fields. The first non-completed step gets the "active"
  // highlight so the admin can see what to do next; everything after stays "future".
  // No clickable navigation: all sections render at once, so the stepper is a progress
  // map, not a tab bar.
  const steps: StepperStep[] = useMemo(() => {
    // Selecting the rule completes this step. The decision point shares the section but is
    // OPTIONAL — publish stopped requiring it (a clip already cut to the moment needs no pause
    // marker), so gating the tick on `decisionSec > 0` left step 2 permanently unticked on a
    // clip that was perfectly publishable.
    const tagRuleDone = Boolean(watchedRule);
    const questionDone =
      correctIndex >= 0 && watchedOptions.filter((o) => o.value.trim()).length >= 2;
    // Commentary is now part of the admin read shape, so we treat the step as done when
    // the field has actual content (pre-filled from the server OR just typed in) — not
    // "the user touched it this session". The old dirty-fields signal would have re-marked
    // the step incomplete every time the editor remounted on a published clip.
    const commentaryDone = watchedCommentary.trim().length > 0;
    const published = clip.published_at !== null;
    const flags = [true, tagRuleDone, questionDone, commentaryDone, published];

    function stateFor(idx: number): StepperStep['state'] {
      if (flags[idx]) return 'completed';
      // Active = the first not-done step (any earlier step still pending → this one is future).
      const earlierAllDone = flags.slice(0, idx).every(Boolean);
      return earlierAllDone ? 'active' : 'future';
    }
    return [
      { number: 1, label: t('admin.clips.editor.stepUpload'), state: stateFor(0) },
      { number: 2, label: t('admin.clips.editor.stepTagRule'), state: stateFor(1) },
      { number: 3, label: t('admin.clips.editor.stepQuestionOptions'), state: stateFor(2) },
      { number: 4, label: t('admin.clips.editor.stepCommentary'), state: stateFor(3) },
      { number: 5, label: t('admin.clips.editor.stepPublish'), state: stateFor(4) },
    ];
  }, [watchedRule, correctIndex, watchedOptions, watchedCommentary, clip.published_at, t]);

  // Build a partial-PATCH payload — only fields the user actually dirtied get sent, so
  // saving "just the title" leaves everything else untouched on the server. correct_decision
  // is resolved from correct_index at submit time.
  function buildTagPayload(values: FormValues): TagClipInput | null {
    const patch: Record<string, unknown> = {};
    if (dirtyFields.title) patch['title'] = values.title;
    if (dirtyFields.description) patch['description'] = values.description || undefined;
    if (dirtyFields.rule_article_id) patch['rule_article_id'] = values.rule_article_id;
    if (dirtyFields.decision_timestamp_sec) {
      patch['decision_timestamp_ms'] = Math.round(values.decision_timestamp_sec * 1000);
    }
    // Options: if any row was edited, send the whole cleaned array (filter blanks).
    if (Array.isArray(dirtyFields.options) && dirtyFields.options.length > 0) {
      patch['options'] = values.options.map((o) => o.value.trim()).filter(Boolean);
    }
    // correct_index isn't a server field — we resolve it to correct_decision (the option's
    // text). Only include in the patch when the admin actually changed which option is
    // correct OR edited any option's label (a renamed correct option implicitly updates
    // correct_decision too). Without this gate, every save would re-write the same
    // correct_decision value the admin saw pre-filled — harmless but wasteful and noisy
    // in the audit fields list.
    const correctDecisionDirty =
      dirtyFields.correct_index === true ||
      (Array.isArray(dirtyFields.options) && dirtyFields.options.length > 0);
    if (correctDecisionDirty && values.correct_index >= 0 && values.options[values.correct_index]) {
      patch['correct_decision'] = values.options[values.correct_index]!.value.trim();
    }
    if (dirtyFields.commentary_text) patch['commentary_text'] = values.commentary_text;

    if (Object.keys(patch).length === 0) return null;
    // Reuse the shared Zod schema to validate on the client too — gives us the same
    // 'at least one field' guarantee the API enforces, with clearer error messages.
    const parsed = tagClipSchema.safeParse(patch);
    if (!parsed.success) {
      throw new Error(parsed.error.issues[0]?.message ?? t('admin.clips.editor.invalidFormValues'));
    }
    return parsed.data;
  }

  const isPublished = clip.published_at !== null;

  // Single save handler used in both draft and published modes — the partial PATCH only
  // sends dirty fields, so saving a published clip is a normal tag write (no unpublish/
  // republish dance needed when an admin fixes a typo in commentary or tweaks an option).
  // The toast label adapts to the current state so the operator knows whether changes are
  // immediately visible to referees ("Changes saved" on a live clip) or staged for publish.
  const onSave = handleSubmit(async (values) => {
    try {
      const payload = buildTagPayload(values);
      if (!payload) {
        notifySuccess(t('admin.clips.editor.publish.nothingToSaveToast'));
        return;
      }
      await tag.mutateAsync(payload);
      notifySuccess(
        isPublished ? t('admin.clips.editor.publish.changesSavedToast') : t('admin.clips.editor.publish.draftSavedToast'),
      );
      form.reset(values); // marks dirty fields clean so next save only sends new edits
    } catch (err) {
      notifyError(
        err,
        isPublished ? t('admin.clips.editor.publish.saveErrorPublished') : t('admin.clips.editor.publish.saveErrorDraft'),
      );
    }
  });

  const onPublish = handleSubmit(async (values) => {
    // Save any pending edits first so the server has the latest tag data before the
    // publish gate runs. The API still re-checks fully-tagged-ness server-side.
    try {
      const payload = buildTagPayload(values);
      if (payload) {
        await tag.mutateAsync(payload);
      }
      await publish.mutateAsync();
      notifySuccess(t('admin.clips.editor.publish.publishedToast'));
    } catch (err) {
      notifyError(err, t('admin.clips.editor.publish.publishErrorToast'));
    }
  });

  const isSaving = tag.isPending || publish.isPending;

  // Not optimistic on purpose: pinning reassigns OTHER referees' pending daily assignments
  // server-side (see pin_daily_clip's SQL), so an instant local flip could show "on" for a
  // toggle that actually failed mid-flight and left assignments untouched. The round-trip is
  // fast enough that the flip lagging behind the click a beat is the honest trade.
  const onTogglePin = async (next: boolean) => {
    try {
      if (next) {
        await pin.mutateAsync();
        notifySuccess(t('admin.clips.editor.publish.pinnedToast'));
      } else {
        await unpin.mutateAsync();
        notifySuccess(t('admin.clips.editor.publish.unpinnedToast'));
      }
    } catch (err) {
      notifyError(
        err,
        next ? t('admin.clips.editor.publish.pinErrorToast') : t('admin.clips.editor.publish.unpinErrorToast'),
      );
    }
  };

  return (
    <>
      {/* Top crumb — small back-link, replaces the heavy PageHeader so the stepper leads. */}
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-1.5 text-[12px] text-muted hover:text-ink"
      >
        <Icon.arrow className="w-3.5 h-3.5 rotate-180" />
        {t('admin.clips.review.backToClips')}
      </button>

      <div className="mb-3 flex items-end justify-between gap-5">
        <div>
          <h1 className="font-display text-[26px] leading-[1.1] text-ink">
            {watchedTitle || clip.title}
          </h1>
          <p className="mt-1 text-[12.5px] text-muted">
            {t('admin.clips.editor.editSub')}
          </p>
        </div>
      </div>

      <Stepper steps={steps} className="mb-5" />

      {/* All sections always visible — the stepper above is a progress indicator only.
          Two-column layout matches the design: authoring sections on the left, Publish
          + Schedule cards in a sticky right column. */}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
        <div className="flex flex-col gap-5">
          <DetailsSection register={register} errors={errors} />
          <VideoFileSection clip={clip} decisionSec={decisionSec} setValue={setValue} />
          <TagRuleSection
            rules={rules}
            register={register}
            errors={errors}
            selectedRule={selectedRule}
          />
          <DecisionSection
            control={control}
            register={register}
            options={options}
            correctIndex={correctIndex}
            errors={errors}
          />
          <CommentarySection register={register} />
          <TagsSection clip={clip} />
        </div>

        <aside className="flex flex-col gap-5 lg:sticky lg:top-4 lg:self-start">
          <PublishPanel
            clip={clip}
            federationName={federationName}
            selectedRuleNumber={selectedRule?.article_number}
            isSaving={isSaving}
            isPublished={isPublished}
            onSave={onSave}
            onPublish={onPublish}
            onUnpublish={() => setDestructive('unpublish')}
            onDelete={() => setDestructive('delete')}
            isPinnedToday={clip.is_daily_clip_today ?? false}
            isPinning={pin.isPending || unpin.isPending}
            onTogglePin={onTogglePin}
          />
          <ScheduleStub />
        </aside>
      </div>

      {/* Destructive dialogs — gated on the editor's view of published. The API still
          rechecks (publish-state could race with another admin in another tab), and the
          dialog surfaces the 409 via the toast. */}
      {destructive === 'unpublish' && (
        <UnpublishClipDialog
          open
          onClose={() => setDestructive(null)}
          clipId={clip.id}
          clipTitle={clip.title}
        />
      )}
      {destructive === 'delete' && (
        <DeleteClipDialog
          open
          onClose={() => setDestructive(null)}
          clipId={clip.id}
          clipTitle={clip.title}
          // After a successful delete the editor route is dead — bounce back to the list.
          onDeleted={() => navigate('/admin/clips')}
        />
      )}
    </>
  );
}

// ─── Clip details ─ title + description ─────────────────────────────────────────────

// Editable clip identity. The title names the clip in every list and defaults to the uploaded
// filename, so this is where the admin gives it a real name (the upload flow no longer asks for
// one). Kept as its own card above the video so it's the first thing after landing in the editor.
// buildTagPayload already PATCHes title/description when dirtied — this just provides the inputs.
function DetailsSection({
  register,
  errors,
}: {
  register: ReturnType<typeof useForm<FormValues>>['register'];
  errors: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <div className="text-[13px] font-semibold text-ink">{t('admin.clips.editor.details.title')}</div>
      <div className="mt-0.5 text-[12px] text-muted">{t('admin.clips.editor.details.sub')}</div>

      <div className="mt-3 flex flex-col gap-3">
        <Field
          label={t('admin.clips.editor.details.titleLabel')}
          htmlFor="title"
          error={(errors['title'] as { message?: string } | undefined)?.message}
        >
          <Input
            id="title"
            placeholder={t('admin.clips.editor.details.titlePlaceholder')}
            {...register('title', { required: t('admin.clips.editor.details.titleRequired') })}
          />
        </Field>

        <Field label={t('admin.clips.editor.details.descriptionLabel')} htmlFor="description">
          <textarea
            id="description"
            rows={2}
            placeholder={t('admin.clips.editor.details.descriptionPlaceholder')}
            className="w-full rounded-sm border border-line-2 bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-ink-3"
            {...register('description')}
          />
        </Field>
      </div>
    </section>
  );
}

// ─── Section 1 ─ video file ─────────────────────────────────────────────────────────

interface VideoFileSectionProps {
  clip: ClipPlayback;
  decisionSec: number;
  setValue: UseFormSetValue<FormValues>;
}

function VideoFileSection({ clip, decisionSec, setValue }: VideoFileSectionProps) {
  const { t } = useTranslation();
  const badge = fileBadge(t, clip.status);
  const playerRef = useRef<VideoPlayerHandle>(null);
  const hasMark = decisionSec > 0;

  // Mark the decision point off the LIVE playhead — the spec's intended UX (read currentTime,
  // submit in ms). shouldDirty is essential: buildTagPayload only sends fields the user dirtied,
  // so without it a player-set value would never reach the PATCH. Round to 1/100s — finer than
  // that is below what an admin scrubs to, and keeps the readout tidy.
  function onMark() {
    const sec = (playerRef.current?.getCurrentTimeMs() ?? 0) / 1000;
    setValue('decision_timestamp_sec', Math.round(sec * 100) / 100, {
      shouldDirty: true,
      shouldValidate: true,
    });
    playerRef.current?.pause(); // freeze on the marked frame so the admin sees exactly what they picked
  }

  // Jump back to the marked point + pause, so the admin can verify the call lands on the right frame.
  function onJump() {
    playerRef.current?.seekToMs(decisionSec * 1000);
    playerRef.current?.pause();
  }

  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <SectionHeader
        number={1}
        title={t('admin.clips.editor.video.sectionTitle')}
        sub={t('admin.clips.editor.video.sectionSub')}
        badge={badge}
      />

      {clip.playback_url ? (
        <div className="mt-3 flex flex-col gap-3">
          {/* Suspense covers the lazy Video.js chunk load — a 16:9 placeholder holds the
              layout so the section doesn't jump when the player resolves. */}
          <Suspense
            fallback={<div className="aspect-video w-full animate-pulse rounded-sm border border-line-2 bg-bg-2" />}
          >
            <VideoPlayer
              ref={playerRef}
              src={clip.playback_url}
              className="overflow-hidden rounded-sm border border-line-2 bg-ink"
            />
          </Suspense>
          {/* Mark / Jump controls + live readout. Mark reads the playhead; Jump seeks back to it. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="primary" size="sm" onClick={onMark}>
              <Icon.clip className="w-3.5 h-3.5" />
              {t('admin.clips.editor.video.markButton')}
            </Button>
            <Button type="button" size="sm" onClick={onJump} disabled={!hasMark}>
              {t('admin.clips.editor.video.jumpButton')}
            </Button>
            <span className="text-[12px] text-muted">
              {hasMark ? (
                <>
                  {t('admin.clips.editor.video.markedAt')}{' '}
                  <span className="font-mono text-ink">{formatTimecode(decisionSec)}</span>
                </>
              ) : (
                t('admin.clips.editor.video.noMarkYet')
              )}
            </span>
          </div>
          <div className="text-[11.5px] text-muted">
            <span className="font-mono">{clip.source_s3_key}</span>
            {clip.duration_sec ? ` · ${formatDuration(clip.duration_sec)}` : ''}
          </div>
        </div>
      ) : (
        // Fallback when there's no signed URL (no CloudFront / local dev): keep the metadata
        // tile and point the admin at the manual seconds field in Section 2.
        <>
          <div className="mt-3 flex items-center gap-4 rounded-sm border border-line-2 bg-bg-2 p-3">
            <div className="grid h-16 w-24 flex-none place-items-center rounded-sm bg-ink text-bg">
              <Icon.clip className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-mono text-[12.5px] text-ink">{clip.source_s3_key}</div>
              <div className="mt-1 text-[12px] text-muted">
                {clip.duration_sec ? formatDuration(clip.duration_sec) : '—'}
                {clip.duration_sec ? ' · ' : ''}
                <span className="font-mono">{clip.id.slice(0, 8)}…</span>
              </div>
            </div>
          </div>
          <p className="mt-2 text-[12px] text-muted">
            {t('admin.clips.editor.video.previewUnavailable')}
          </p>
        </>
      )}
    </section>
  );
}

// ─── Section 2 ─ tag the rule ───────────────────────────────────────────────────────

interface TagRuleSectionProps {
  rules: { id: string; article_number: string; title: string }[];
  register: ReturnType<typeof useForm<FormValues>>['register'];
  errors: Record<string, unknown>;
  selectedRule: { article_number: string; title: string } | undefined;
}

function TagRuleSection({ rules, register, errors, selectedRule }: TagRuleSectionProps) {
  const { t } = useTranslation();
  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <SectionHeader
        number={2}
        title={t('admin.clips.editor.tagRule.sectionTitle')}
        sub={t('admin.clips.editor.tagRule.sectionSub')}
      />

      <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Field
          label={t('admin.clips.editor.tagRule.primaryRuleLabel')}
          htmlFor="rule_article_id"
          error={(errors['rule_article_id'] as { message?: string } | undefined)?.message}
        >
          <select
            id="rule_article_id"
            className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
            {...register('rule_article_id')}
          >
            <option value="">{t('admin.clips.editor.tagRule.chooseRule')}</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>
                {t('admin.clips.editor.tagRule.ruleOption', { number: r.article_number, title: r.title })}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('admin.clips.editor.tagRule.decisionPointLabel')} htmlFor="decision_timestamp_sec">
          <Input
            id="decision_timestamp_sec"
            type="number"
            step="0.1"
            min={0}
            placeholder="8.5"
            {...register('decision_timestamp_sec', { valueAsNumber: true, min: 0 })}
          />
        </Field>
      </div>

      {selectedRule && (
        <div className="mt-3 rounded-sm border border-line-2 bg-bg-2 px-3 py-2 text-[12px] text-ink-2">
          <span className="font-mono font-semibold text-ink">
            {t('common.ruleLabel', { number: selectedRule.article_number })}
          </span>{' '}
          · {selectedRule.title}
        </div>
      )}
    </section>
  );
}

// ─── Section 3 ─ decision question & options ────────────────────────────────────────

interface DecisionSectionProps {
  control: Control<FormValues>;
  register: ReturnType<typeof useForm<FormValues>>['register'];
  options: ReturnType<typeof useFieldArray<FormValues, 'options'>>;
  correctIndex: number;
  errors: Record<string, unknown>;
}

function DecisionSection({ control, register, options, correctIndex }: DecisionSectionProps) {
  const { t } = useTranslation();
  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <SectionHeader
        number={3}
        title={t('admin.clips.editor.decision.sectionTitle')}
        sub={t('admin.clips.editor.decision.sectionSub')}
      />

      <Controller
        control={control}
        name="correct_index"
        render={({ field: correctField }) => (
          <div className="mt-3 flex flex-col gap-2">
            {options.fields.map((row, idx) => {
              const isCorrect = correctIndex === idx;
              const letter = String.fromCharCode(65 + idx); // A, B, C, …
              return (
                <div
                  key={row.id}
                  className={
                    'flex items-center gap-3 rounded-sm border-2 border-dashed p-2.5 ' +
                    (isCorrect
                      ? 'border-success bg-success-tint'
                      : 'border-line-2 bg-bg-2')
                  }
                >
                  <div
                    className={
                      'grid h-8 w-8 flex-none place-items-center rounded-sm text-[13px] font-bold ' +
                      (isCorrect
                        ? 'bg-success text-bg'
                        : 'bg-ink text-bg')
                    }
                  >
                    {letter}
                  </div>

                  <input
                    placeholder={t('admin.clips.editor.decision.optionPlaceholder', { letter })}
                    className="h-9 min-w-0 flex-1 rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
                    {...register(`options.${idx}.value` as const)}
                  />

                  <button
                    type="button"
                    onClick={() => correctField.onChange(idx)}
                    className={
                      'inline-flex items-center gap-1.5 text-[12px] font-medium ' +
                      (isCorrect ? 'text-success' : 'text-muted hover:text-ink')
                    }
                  >
                    <span
                      className={
                        'grid h-4 w-4 place-items-center rounded-full border-[1.5px] ' +
                        (isCorrect ? 'border-success bg-success' : 'border-line-3')
                      }
                    >
                      {isCorrect && <span className="h-1.5 w-1.5 rounded-full bg-bg" />}
                    </span>
                    {t('admin.clips.editor.decision.correct')}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      // If we removed the row that was marked correct, unset the mark so
                      // the form doesn't reference a stale (now shifted) index.
                      if (correctIndex === idx) correctField.onChange(-1);
                      else if (correctIndex > idx) correctField.onChange(correctIndex - 1);
                      options.remove(idx);
                    }}
                    className="text-muted hover:text-danger"
                    aria-label={t('admin.clips.editor.decision.removeOptionAria', { letter })}
                    disabled={options.fields.length <= 2}
                  >
                    <Icon.close className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => options.append({ value: '' })}
              disabled={options.fields.length >= 8}
              className="mt-1 inline-flex w-fit items-center gap-2 rounded-btn border-2 border-dashed border-line-2 px-3 py-1.5 text-[12.5px] font-medium text-muted hover:border-line-3 hover:text-ink disabled:opacity-50"
            >
              <Icon.plus className="w-3.5 h-3.5" />
              {t('admin.clips.editor.decision.addOption')}
            </button>
          </div>
        )}
      />
    </section>
  );
}

// ─── Section 4 ─ commentary ─────────────────────────────────────────────────────────

function CommentarySection({
  register,
}: {
  register: ReturnType<typeof useForm<FormValues>>['register'];
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <SectionHeader
        number={4}
        title={t('admin.clips.editor.commentary.sectionTitle')}
        sub={t('admin.clips.editor.commentary.sectionSub')}
      />
      <textarea
        rows={5}
        placeholder={t('admin.clips.editor.commentary.placeholder')}
        className="mt-3 w-full rounded-sm border border-line-2 bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-ink-3"
        {...register('commentary_text')}
      />
    </section>
  );
}

// ─── Section ─ managed tags ─────────────────────────────────────────────────────────

// Assign up to MAX_CLIP_TAGS managed-vocabulary tags to the clip, and coin new vocabulary inline.
// Decoupled from the main authoring form's dirty-tracking: each toggle saves immediately via its
// own tagClip({ tag_ids }) call (set semantics), so tags don't ride the rule/options "Save".
// Passing the clip's federation + sport works for both roles — a federation_admin's are JWT-derived
// (the ids are ignored server-side), a super_admin's console flow needs them explicit.
function TagsSection({ clip }: { clip: ClipPlayback }) {
  const { t } = useTranslation();
  // An Official clip has no federation (federation_id null) → no federation-scoped tags apply; pass
  // undefined so the tags query is a clean no-op rather than matching an empty string.
  const tagsQuery = useClipTagsQuery({ federationId: clip.federation_id ?? undefined, sportId: clip.sport_id });
  const setTags = useSetClipTags(clip.id);
  const createTag = useCreateClipTag();
  const [newLabel, setNewLabel] = useState('');
  // Colour chosen for the tag being coined — defaults to the neutral swatch.
  const [newColor, setNewColor] = useState<ClipTagColor>('slate');

  const vocabulary = tagsQuery.data ?? [];
  // Current assignment comes off the clip payload — which the optimistic mutation patches instantly
  // on toggle (then reconciles on settle), so the chips flip with no round-trip lag.
  const current = useMemo(() => clip.tags ?? [], [clip.tags]);
  const selectedIds = useMemo(() => new Set(current.map((t) => t.id)), [current]);
  const atCap = selectedIds.size >= MAX_CLIP_TAGS;

  // Persist a new tag set (full objects so the optimistic patch can render labels/colours). No
  // success toast — the instant chip flip is the confirmation; only a failure (auto-rolled-back
  // in the hook) is surfaced.
  async function save(nextTags: EmbeddedClipTag[]) {
    try {
      await setTags.mutateAsync(nextTags);
    } catch (err) {
      notifyError(err, t('admin.clips.editor.tags.updateErrorToast'));
    }
  }

  function toggle(tag: ClipTag) {
    const has = selectedIds.has(tag.id);
    if (has) {
      void save(current.filter((x) => x.id !== tag.id));
      return;
    }
    if (atCap) {
      notifyWarning(t('admin.clips.editor.tags.capToast', { max: MAX_CLIP_TAGS }));
      return;
    }
    void save([...current, { id: tag.id, label: tag.label, color: tag.color }]);
  }

  async function addLabel() {
    const label = newLabel.trim();
    if (!label) return;
    try {
      const created = await createTag.mutateAsync({
        label,
        color: newColor,
        federation_id: clip.federation_id ?? undefined,
        sport_id: clip.sport_id,
      });
      setNewLabel('');
      setNewColor('slate'); // reset the swatch for the next tag
      notifySuccess(t('admin.clips.editor.tags.addedToast', { label: created.label }));
      // Convenience: auto-assign the freshly coined tag when there's still room (the common intent
      // is "tag this clip with a new label"). Over the cap, it just joins the vocabulary.
      if (!atCap) void save([...current, { id: created.id, label: created.label, color: created.color }]);
    } catch (err) {
      notifyError(err, t('admin.clips.editor.tags.createErrorToast'));
    }
  }

  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <SectionHeader
        number={5}
        title={t('admin.clips.editor.tags.sectionTitle')}
        sub={t('admin.clips.editor.tags.sectionSub', { max: MAX_CLIP_TAGS })}
      />

      <div className="mt-3">
        {tagsQuery.isLoading ? (
          <Spinner className="h-4 w-4 text-muted" />
        ) : vocabulary.length === 0 ? (
          <p className="text-[12.5px] text-muted">{t('admin.clips.editor.tags.noneYet')}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {vocabulary.map((vt) => {
              const on = selectedIds.has(vt.id);
              const disabled = !on && atCap;
              return (
                // Chip + its delete control are SIBLINGS in a wrapper, never nested: the chip is
                // itself a <button>, and a button inside a button is invalid HTML (React will warn,
                // and the inner click target behaves inconsistently across browsers).
                <span key={vt.id} className="inline-flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggle(vt)}
                  // Not disabled while the save is in flight — the chip flips optimistically, so the
                  // control stays snappy and re-clickable; a failed write rolls the state back.
                  disabled={disabled}
                  aria-pressed={on}
                  // Colour comes from the tag's palette key (solid when applied, outline when
                  // available); a capped-out chip drops the colour for a plain muted look. Inline
                  // style over Tailwind so arbitrary swatch hexes survive the purge.
                  style={disabled ? undefined : tagChipStyle(vt.color, on)}
                  className={[
                    'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12px] font-medium transition-colors',
                    disabled ? 'cursor-not-allowed border-line-2 bg-surface text-muted opacity-60' : '',
                  ].join(' ')}
                >
                  {on && <Icon.check className="h-3.5 w-3.5" />}
                  {vt.label}
                </button>
                <DeleteTagButton tag={vt} />
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Coin a new vocabulary label + pick its colour. Enter or the button submits; the input is a
          plain field (this section lives outside the RHF form on purpose). */}
      <div className="mt-3 flex items-center gap-2">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addLabel();
            }
          }}
          maxLength={40}
          placeholder={t('admin.clips.editor.tags.newTagPlaceholder')}
          className="max-w-[220px]"
        />
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={() => void addLabel()}
          disabled={createTag.isPending || newLabel.trim().length === 0}
        >
          {createTag.isPending ? (
            <Spinner className="w-4 h-4" />
          ) : (
            <>
              <Icon.plus className="h-3.5 w-3.5" /> {t('admin.clips.editor.tags.addTagButton')}
            </>
          )}
        </Button>
      </div>

      {/* Swatch picker for the new tag's colour — a row of colour dots, the chosen one ringed. */}
      <div className="mt-2 flex items-center gap-1.5">
        <span className="mr-1 text-[11px] text-muted">{t('admin.clips.editor.tags.colourLabel')}</span>
        {TAG_COLOR_OPTIONS.map((c) => {
          const selected = c.key === newColor;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setNewColor(c.key)}
              aria-label={t('admin.clips.editor.tags.colourAria', { key: c.key })}
              aria-pressed={selected}
              style={{ backgroundColor: c.hex }}
              className={[
                'h-5 w-5 rounded-full ring-offset-1 ring-offset-surface transition-shadow',
                selected ? 'ring-2 ring-ink' : 'ring-1 ring-line-2 hover:ring-line-3',
              ].join(' ')}
            />
          );
        })}
      </div>
      <p className="mt-1.5 text-[11px] text-muted">
        {t('admin.clips.editor.tags.countSuffix', { selected: selectedIds.size, max: MAX_CLIP_TAGS })}
      </p>
    </section>
  );
}

// Remove a label from the federation's vocabulary entirely — distinct from the chip beside it,
// which only assigns/unassigns the tag on THIS clip. Deleting is federation-wide and cascades: the
// tag disappears from every clip carrying it and from the referee library's filter chips. That's
// irreversible and affects clips the admin isn't looking at, so it goes behind a confirm rather
// than a bare click (the same AlertDialog pattern the sports/federations consoles use).
function DeleteTagButton({ tag }: { tag: ClipTag }) {
  const { t } = useTranslation();
  const del = useDeleteClipTag();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await del.mutateAsync(tag.id);
      notifySuccess(t('admin.clips.editor.deleteTag.deletedToast', { label: tag.label }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.clips.editor.deleteTag.errorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          aria-label={t('admin.clips.editor.deleteTag.deleteAria', { label: tag.label })}
          className="grid h-5 w-5 flex-none place-items-center rounded-full text-muted transition-colors hover:bg-danger-tint hover:text-danger"
        >
          <Icon.close className="h-3 w-3" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.clips.editor.deleteTag.confirmTitle', { label: tag.label })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.clips.editor.deleteTag.confirmBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={del.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={() => void confirm()} disabled={del.isPending}>
            {del.isPending ? <Spinner className="w-4 h-4" /> : t('admin.clips.editor.deleteTag.deleteButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Section 5 (right) ─ publish panel ──────────────────────────────────────────────

interface PublishPanelProps {
  clip: ClipPlayback;
  federationName: string;
  selectedRuleNumber: string | undefined;
  isSaving: boolean;
  isPublished: boolean;
  onSave: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  // "Make this the daily clip" — the first of the 4 design toggles to go live (an override
  // for select_daily_clip's random pick). The other 3 (discussion/XP/trainee-visibility
  // toggles) stay stubbed below; see stubbedToggles.
  isPinnedToday: boolean;
  isPinning: boolean;
  onTogglePin: (next: boolean) => void;
}

function PublishPanel({
  clip,
  federationName,
  selectedRuleNumber,
  isSaving,
  isPublished,
  onSave,
  onPublish,
  onUnpublish,
  onDelete,
  isPinnedToday,
  isPinning,
  onTogglePin,
}: PublishPanelProps) {
  const { t } = useTranslation();
  // The remaining 3 toggles in the design map to features we haven't built yet (M7 per-clip
  // discussion on/off, M9 gamification). Render them visible-but-disabled with a "coming with …"
  // label so the IA stays consistent with the design without making promises we can't keep.
  const stubbedToggles: { label: string; sub: string; when: string }[] = [
    { label: t('admin.clips.editor.stub.discussionLabel'), sub: t('admin.clips.editor.stub.discussionSub'), when: 'M9' },
    { label: t('admin.clips.editor.stub.xpLabel'), sub: t('admin.clips.editor.stub.xpSub'), when: 'M9' },
    { label: t('admin.clips.editor.stub.traineeLabel'), sub: t('admin.clips.editor.stub.traineeSub'), when: 'M9' },
  ];

  // An official (platform) clip has no federation, so it can never be "today's clip" for
  // anyone — pinClip rejects it server-side (400 / CLIP_NOT_FEDERATION); the toggle disables
  // client-side for the same reason rather than letting the admin discover it via an error.
  const pinDisabledReason = clip.federation_id === null
    ? t('admin.clips.editor.publish.pinDisabledNoFederation')
    : !isPublished
      ? t('admin.clips.editor.publish.pinDisabledUnpublished')
      : undefined;

  const draftBadge = isPublished
    ? 'bg-success-tint text-success'
    : 'bg-warn-tint text-warn';

  return (
    <section className="rounded border border-line bg-surface p-5 shadow-1">
      <SectionHeader
        number={5}
        title={t('admin.clips.editor.stepPublish')}
        sub={isPublished ? t('admin.clips.editor.publish.subLive') : t('admin.clips.editor.publish.subReady')}
        badge={{
          label: isPublished ? statusLabel(t, 'published') : statusLabel(t, 'draft'),
          klass: draftBadge,
          spin: false,
        }}
      />

      <div className="mt-3 flex flex-col gap-3">
        <div
          className="flex items-start justify-between gap-3"
          title={pinDisabledReason}
        >
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-ink">{t('admin.clips.editor.publish.pinLabel')}</div>
            <div className="mt-0.5 text-[11.5px] text-muted">
              {isPinnedToday ? t('admin.clips.editor.publish.pinSubOn') : t('admin.clips.editor.publish.pinSubOff')}
            </div>
          </div>
          <Toggle
            checked={isPinnedToday}
            onChange={onTogglePin}
            disabled={Boolean(pinDisabledReason) || isPinning}
            label={t('admin.clips.editor.publish.pinLabel')}
          />
        </div>
        {stubbedToggles.map((s) => (
          <StubbedToggle key={s.label} label={s.label} sub={s.sub} when={s.when} />
        ))}
      </div>

      <div className="my-4 border-t border-line" />

      <dl className="flex flex-col gap-1.5 text-[13px]">
        <SummaryRow label={t('admin.clips.review.ruleLabel')} value={selectedRuleNumber ?? '—'} />
        <SummaryRow label={t('admin.clips.review.durationLabel')} value={clip.duration_sec ? formatDuration(clip.duration_sec) : '—'} />
        <SummaryRow label={t('admin.clips.editor.publish.statusLabel')} value={clip.status} mono />
        <SummaryRow label={t('admin.clips.review.federationLabel')} value={federationName} />
      </dl>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button onClick={onSave} disabled={isSaving}>
          {/* Label is state-aware: published clips skip the "draft" wording so the operator
              sees that clicking immediately ships the edit to referees, not to a draft. */}
          {isSaving ? (
            <Spinner className="w-3.5 h-3.5" />
          ) : isPublished ? (
            t('admin.clips.editor.publish.saveChanges')
          ) : (
            t('admin.clips.editor.publish.saveDraft')
          )}
        </Button>
        {/* Primary action toggles meaning with state. Draft → Publish (accent). Live →
            Unpublish (primary ink, to stand out from the persistent danger Delete below
            without screaming). The button positions stay the same so the eye lands in
            the same spot in both modes. */}
        {isPublished ? (
          <Button variant="primary" onClick={onUnpublish} disabled={isSaving}>
            <Icon.eyeOff className="w-3.5 h-3.5" />
            {t('admin.clips.review.unpublish')}
          </Button>
        ) : (
          <Button variant="accent" onClick={onPublish} disabled={isSaving}>
            {t('admin.clips.editor.publish.publish')} <Icon.arrow className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {/* Destructive footer — set apart from the publish row by a divider so it doesn't
          live next to the primary CTA (avoids misclicks). Gated on !isPublished so a live
          clip can't be deleted without an explicit unpublish first; the tooltip explains why. */}
      <div className="mt-5 border-t border-line pt-4">
        <Button
          variant="danger"
          size="sm"
          onClick={onDelete}
          disabled={isPublished || isSaving}
          title={isPublished ? t('admin.clips.editor.publish.deleteBlockedTooltip') : undefined}
        >
          <Icon.trash className="w-3.5 h-3.5" />
          {t('admin.clips.editor.publish.deleteClip')}
        </Button>
        {isPublished && (
          <p className="mt-1.5 text-[11.5px] text-muted">
            {t('admin.clips.editor.publish.deleteBlockedNote')}
          </p>
        )}
      </div>
    </section>
  );
}

function StubbedToggle({ label, sub, when }: { label: string; sub: string; when: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-start justify-between gap-3 opacity-60"
      title={t('admin.clips.editor.stub.comingWithTitle', { when })}
    >
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-ink">{label}</div>
        <div className="mt-0.5 text-[11.5px] text-muted">
          {sub} · <span className="font-mono text-muted-2">{t('admin.clips.editor.stub.comingWith', { when })}</span>
        </div>
      </div>
      {/* Visual switch — disabled and locked off so it's clear nothing happens. */}
      <span className="mt-0.5 inline-flex h-5 w-9 flex-none items-center rounded-full bg-line-2 px-0.5">
        <span className="block h-4 w-4 rounded-full bg-surface shadow-1" />
      </span>
    </div>
  );
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className={'truncate ' + (mono ? 'font-mono text-[12px]' : '') + ' text-ink'}>{value}</dd>
    </div>
  );
}

// ─── Schedule card stub (right column, below Publish) ───────────────────────────────

function ScheduleStub() {
  const { t } = useTranslation();
  return (
    <section className="rounded border border-dashed border-line-2 bg-bg-2 p-4">
      <div className="flex items-start gap-3">
        <div className="grid h-8 w-8 flex-none place-items-center rounded-sm bg-accent-tint text-accent">
          <Icon.spark className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-ink">{t('admin.clips.editor.schedule.title')}</div>
          <p className="mt-0.5 text-[11.5px] text-muted">
            {t('admin.clips.editor.schedule.sub')}
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Small atoms ────────────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  number: number;
  title: string;
  sub: string;
  badge?: { label: string; klass: string; spin: boolean };
}

function SectionHeader({ number, title, sub, badge }: SectionHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-[13px] font-semibold text-ink">
          <span className="font-mono text-muted">{number} ·</span> {title}
        </div>
        <div className="mt-0.5 text-[12px] text-muted">{sub}</div>
      </div>
      {badge && (
        <span
          className={
            'inline-flex h-[22px] flex-none items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium ' +
            badge.klass
          }
        >
          {badge.spin ? <Spinner className="w-2.5 h-2.5" /> : <Icon.check className="w-3 h-3" />}
          {badge.label}
        </span>
      )}
    </div>
  );
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// m:ss.cc — sub-second timecode for the decision-point readout (formatDuration is whole-second).
// Carry-safe: works off total centiseconds so e.g. 8.999s never renders as ":08.100".
function formatTimecode(sec: number): string {
  const totalCs = Math.max(0, Math.round(sec * 100));
  const m = Math.floor(totalCs / 6000);
  const s = Math.floor((totalCs % 6000) / 100);
  const cs = totalCs % 100;
  return `${m}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}
